import { SessionDataType } from './contexts/SessionData';
import { UserDataType } from './contexts/UserData';

export const SHOPPING_CART_STORAGE_KEY = 'shoppingCart';
export const ORDER_DATA_STORAGE_KEY = 'orderData';
export const USER_DATA_STORAGE_KEY = 'userData';

export type StoredCartProductItem = {
  product_key: string;
  product_version: string | null;
  combo_product_list: string[] | null;
  adds: string[] | null;
  total_cost: number;
  quantity: number;
};

export type ShoppingCartStorage = Record<string, StoredCartProductItem[]>;

type ShoppingCartStorageReadResult = {
  storage: ShoppingCartStorage;
  wasChanged: boolean;
};

const isPlainObject = (value: unknown): value is Record<string, unknown> => (
  Boolean(value) && typeof value === 'object' && !Array.isArray(value)
);

const readJsonObjectStorage = (key: string): Record<string, unknown> => {
  try {
    const rawValue = window.localStorage.getItem(key);
    if (!rawValue) {
      return {};
    }

    const parsedValue: unknown = JSON.parse(rawValue);
    return isPlainObject(parsedValue) ? parsedValue : {};
  } catch {
    return {};
  }
};

export const readSessionDataStorage = (): SessionDataType => (
  readJsonObjectStorage(ORDER_DATA_STORAGE_KEY) as SessionDataType
);

export const readUserDataStorage = (): UserDataType => (
  readJsonObjectStorage(USER_DATA_STORAGE_KEY) as UserDataType
);

const normalizeNullableStringArray = (value: unknown): string[] | null | undefined => {
  if (value === null) {
    return null;
  }

  if (!Array.isArray(value)) {
    return undefined;
  }

  const stringValues = value.filter((item): item is string => typeof item === 'string');
  return stringValues.length === value.length ? stringValues : undefined;
};

const normalizeNullableString = (value: unknown): string | null | undefined => {
  if (value === null) {
    return null;
  }

  return typeof value === 'string' ? value : undefined;
};

/**
 * normalizeCartItem
 *
 * Converts one persisted cart row into the current cart item shape. Browser
 * storage can outlive deployments and can be manually edited, so every field
 * used by checkout rendering and submit validation is checked before reuse.
 */
const normalizeCartItem = (value: unknown): StoredCartProductItem | null => {
  if (!isPlainObject(value)) {
    return null;
  }

  const productKey = value.product_key;
  const productVersion = normalizeNullableString(value.product_version);
  const comboProductList = normalizeNullableStringArray(value.combo_product_list);
  const adds = normalizeNullableStringArray(value.adds);
  const totalCost = value.total_cost;
  const quantity = value.quantity;

  if (
    typeof productKey !== 'string'
    || productKey.trim().length === 0
    || productVersion === undefined
    || comboProductList === undefined
    || adds === undefined
    || typeof totalCost !== 'number'
    || !Number.isFinite(totalCost)
    || typeof quantity !== 'number'
    || !Number.isFinite(quantity)
    || quantity <= 0
  ) {
    return null;
  }

  return {
    product_key: productKey,
    product_version: productVersion,
    combo_product_list: comboProductList,
    adds,
    total_cost: totalCost,
    quantity,
  };
};

const normalizeCart = (value: unknown): { cart: StoredCartProductItem[]; wasChanged: boolean } => {
  if (!Array.isArray(value)) {
    return { cart: [], wasChanged: value !== undefined };
  }

  const cart = value.reduce<StoredCartProductItem[]>((acc, item) => {
    const normalizedItem = normalizeCartItem(item);
    if (normalizedItem) {
      acc.push(normalizedItem);
    }
    return acc;
  }, []);

  return {
    cart,
    wasChanged: cart.length !== value.length,
  };
};

const normalizeCartRecord = (value: unknown): ShoppingCartStorageReadResult => {
  if (!isPlainObject(value)) {
    return { storage: {}, wasChanged: value !== undefined };
  }

  return Object.entries(value).reduce<ShoppingCartStorageReadResult>((result, [foodMakerKey, cartValue]) => {
    if (typeof foodMakerKey !== 'string' || foodMakerKey.trim().length === 0) {
      result.wasChanged = true;
      return result;
    }

    const normalizedCart = normalizeCart(cartValue);
    result.storage[foodMakerKey] = normalizedCart.cart;
    result.wasChanged = result.wasChanged || normalizedCart.wasChanged;
    return result;
  }, { storage: {}, wasChanged: false });
};

export const readShoppingCartStorage = (ttlMs: number): ShoppingCartStorageReadResult => {
  try {
    const rawValue = window.localStorage.getItem(SHOPPING_CART_STORAGE_KEY);
    if (!rawValue) {
      return { storage: {}, wasChanged: false };
    }

    const parsedValue: unknown = JSON.parse(rawValue);
    if (!isPlainObject(parsedValue)) {
      return { storage: {}, wasChanged: true };
    }

    if (Object.prototype.hasOwnProperty.call(parsedValue, 'carts')) {
      const updateTime = typeof parsedValue.update_time === 'number' ? parsedValue.update_time : 0;
      if (Date.now() - updateTime > ttlMs) {
        return { storage: {}, wasChanged: false };
      }

      return normalizeCartRecord(parsedValue.carts);
    }

    return normalizeCartRecord(parsedValue);
  } catch {
    return { storage: {}, wasChanged: true };
  }
};

export const saveShoppingCartStorage = (storage: ShoppingCartStorage): void => {
  window.localStorage.setItem(SHOPPING_CART_STORAGE_KEY, JSON.stringify({
    update_time: Date.now(),
    carts: storage,
  }));
};
